import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SocialShare from "@/components/feature/SocialShare";
import CommentSystem from "@/components/feature/CommentSystem";
import AdBanner, { AdSidebar } from "@/components/feature/AdBanner";
import { useNewsBySlug, useNews } from "@/hooks/useSupabaseData";
import { useNewsAnalytics } from "@/hooks/useAnalytics";
import { usePageSeo } from "@/hooks/usePageSeo";

const SITE_URL = "https://centralalvinegra.com.br";
const DEFAULT_NEWS_IMAGE = "https://i.ibb.co/3yGH0K9S/Logo-Central-Alvingera.png";

function cleanMetaText(value?: string | null) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function newsSeoDescription(news?: { meta_description?: string | null; subtitle?: string | null; content?: string | null }) {
  return cleanMetaText(news?.meta_description || news?.subtitle || news?.content).slice(0, 220) ||
    "Noticias do Botafogo, SAF, mercado da bola, Brasileirao, jogos e Botafogo TV.";
}

function newsCategories(news: { category: string; categories?: string[] | null }) {
  return Array.from(new Set([news.category, ...(news.categories || [])].filter(Boolean)));
}

function shareCategory(a: { category: string; categories?: string[] | null }, b: { category: string; categories?: string[] | null }) {
  const currentCategories = newsCategories(a);
  return newsCategories(b).some((category) => currentCategories.includes(category));
}

export default function NewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [currentImage, setCurrentImage] = useState(0);

  const { news, loading } = useNewsBySlug(slug || "");
  const { news: allNews } = useNews({ limit: 20 });
  useNewsAnalytics(news?.id || "", news?.slug);
  const newsUrl = news?.slug ? `${SITE_URL}/noticias/${news.slug}` : `${SITE_URL}/noticias`;
  const newsTitle = news?.title || "Central Alvinegra | Noticias do Botafogo";
  const newsDescription = newsSeoDescription(news);
  const newsImage = news?.image_url?.trim() || DEFAULT_NEWS_IMAGE;

  usePageSeo({
    title: newsTitle,
    description: newsDescription,
    image: newsImage,
    url: newsUrl,
    type: "article",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 px-4 md:px-8">
          <div className="animate-pulse max-w-4xl mx-auto space-y-4">
            <div className="h-8 w-32 bg-alvinegro-light rounded-full"></div>
            <div className="h-12 bg-alvinegro-light rounded-lg"></div>
            <div className="h-6 bg-alvinegro-light rounded w-3/4"></div>
            <div className="aspect-video bg-alvinegro-light rounded-2xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 px-4 md:px-8 text-center">
          <h1 className="text-2xl font-bold text-alvinegro-black mb-4">Notícia não encontrada</h1>
          <button
            onClick={() => navigate("/noticias")}
            className="bg-alvinegro-black text-white px-6 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            Ver todas as notícias
          </button>
        </div>
      </div>
    );
  }

  const relatedNews = allNews.filter(
    (n) => n.id !== news.id && (shareCategory(n, news) || (n.tags && news.tags && n.tags.some((t) => news.tags.includes(t))))
  ).slice(0, 3);

  const sameCategoryNews = allNews.filter(
    (n) => n.id !== news.id && shareCategory(n, news)
  ).slice(0, 3);
  const contentHasHtml = /<\/?[a-z][\s\S]*>/i.test(news.content);
  const renderedContent = contentHasHtml
    ? news.content
    : news.content.replace(/\n/g, '<br/>');
  const sourceUrl = news.source_url?.trim();

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />
      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />
      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: "Início", path: "/" },
              { label: "Notícias", path: "/noticias" },
              { label: news.category, path: `/noticias?categoria=${news.category}` },
              { label: news.title },
            ]}
          />

          {/* Ad Banner - Top */}
          <div className="my-4 md:my-6">
            <AdBanner variant="leaderboard" label="Publicidade" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Category & Tags */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="bg-alvinegro-black text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {news.category}
                </span>
                {news.tags && news.tags.map((tag) => (
                  <span key={tag} className="bg-alvinegro-light text-alvinegro-dark text-xs px-2.5 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Title */}
              <h1 className="font-serif font-bold text-2xl md:text-4xl lg:text-5xl text-alvinegro-black leading-tight mb-3">
                {news.title}
              </h1>
              <p className="text-alvinegro-muted text-base md:text-lg leading-relaxed mb-6">
                {news.subtitle}
              </p>

              {/* Meta Info */}
              <div className="flex items-center justify-between flex-wrap gap-4 mb-6 pb-6 border-b border-alvinegro-border">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-alvinegro-black flex items-center justify-center">
                    <span className="text-white font-bold text-xs">{(news.author || "RA").substring(0, 2).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-alvinegro-black">{news.author}</p>
                    <p className="text-xs text-alvinegro-muted">
                      {news.published_at ? new Date(news.published_at).toLocaleDateString("pt-BR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }) : ""}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Share */}
              <div className="mb-6">
                <SocialShare title={news.title} url={newsUrl} />
              </div>

              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden aspect-video mb-6">
                <img
                  src={news.image_url || "https://readdy.ai/api/search-image?query=Football%20stadium%20night%20professional%20photography%20dramatic%20lights%20dark%20atmosphere%20black%20and%20white&width=1200&height=675&seq=detail&orientation=landscape"}
                  alt={news.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Ad Banner - Inside Article Top */}
              <AdBanner variant="medium-rectangle" className="mb-6" label="Publicidade" />

              {/* Content */}
              <div className="prose max-w-none">
                <div className="rich-news-content text-base md:text-lg text-alvinegro-dark leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderedContent }}
                />
              </div>

              {/* Source */}
              <div className="mt-8 pt-6 border-t border-alvinegro-border">
                <p className="text-sm text-alvinegro-muted">
                  Fonte: <span className="font-medium text-alvinegro-dark">{news.source || "Central Alvinegra"}</span>
                  {sourceUrl && (
                    <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-alvinegro-black hover:underline">
                      Ver original <i className="ri-external-link-line text-xs"></i>
                    </a>
                  )}
                </p>
              </div>

              {/* Ad Banner - Bottom Article */}
              <AdBanner variant="leaderboard" className="my-8" label="Publicidade" />

              {/* Comments */}
              <CommentSystem newsId={news.id} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              {/* Ad Banner - Sidebar Top */}
              <AdSidebar label="Publicidade" />

              {/* Related News */}
              <div>
                <h3 className="font-serif font-bold text-lg text-alvinegro-black mb-4">
                  Notícias Relacionadas
                </h3>
                <div className="space-y-4">
                  {relatedNews.map((item) => (
                    <div
                      key={item.id}
                      className="group cursor-pointer"
                      onClick={() => navigate(`/noticias/${item.slug}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0">
                          <img
                            src={item.image_url || "https://readdy.ai/api/search-image?query=Football%20match%20action%20professional%20sports%20photography%20dramatic&width=400&height=300&seq=related&orientation=landscape"}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                            {item.title}
                          </h4>
                          <span className="text-xs text-alvinegro-muted">{item.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ad Banner - Sidebar Middle */}
              <AdSidebar label="Publicidade" />

              {/* Same Category */}
              <div>
                <h3 className="font-serif font-bold text-lg text-alvinegro-black mb-4">
                  Mais de {news.category}
                </h3>
                <div className="space-y-4">
                  {sameCategoryNews.map((item) => (
                    <div
                      key={item.id}
                      className="group cursor-pointer"
                      onClick={() => navigate(`/noticias/${item.slug}`)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0">
                          <img
                            src={item.image_url || "https://readdy.ai/api/search-image?query=Football%20match%20action%20professional%20sports%20photography%20dramatic&width=400&height=300&seq=related&orientation=landscape"}
                            alt={item.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                            {item.title}
                          </h4>
                          <span className="text-xs text-alvinegro-muted">
                            {item.published_at ? new Date(item.published_at).toLocaleDateString("pt-BR") : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ad Banner - Sidebar Bottom */}
              <AdSidebar label="Publicidade" />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
